import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, X, CheckCircle, Pencil, Check, Loader2, Download, Smartphone, Trash2, QrCode, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { AppShell } from '../components/layout/AppShell';
import { Card } from '../components/common/Card';
import { AvatarPicker } from '../components/common/AvatarPicker';
import { Button } from '../components/common/Button';
import { IOSInstallGuide } from '../components/common/IOSInstallGuide';
import { api } from '../services/api';
import { trackEvent } from '../utils/analytics';
import type { AsaasStatus } from '../types';

export function SettingsPage() {
  const navigate = useNavigate();
  const { user, subscriber, signOut, setSubscriber } = useAuth();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(subscriber?.name || '');
  const [isSavingName, setIsSavingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const { isInstalled, canInstall, isIOS, install } = useInstallPrompt();

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isEditingName]);

  const handleSaveName = async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === subscriber?.name) {
      setIsEditingName(false);
      return;
    }

    setIsSavingName(true);
    try {
      const response = await api.auth.updateProfile({ name: trimmed });
      if (response.data?.subscriber) {
        setSubscriber(response.data.subscriber);
      }
      setIsEditingName(false);
    } catch (error) {
      console.error('Error updating name:', error);
    } finally {
      setIsSavingName(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <AppShell>
      <div className="space-y-6 max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-display text-2xl font-bold text-white">
            Configurações
          </h1>
        </motion.div>

        {/* Profile */}
        <Card delay={0.1}>
          <div className="flex items-center gap-4">
            <AvatarPicker />
            <div className="flex-1 min-w-0">
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') setIsEditingName(false);
                    }}
                    className="bg-white/10 text-white font-semibold rounded-lg px-2 py-1 text-sm w-full outline-none focus:ring-1 focus:ring-turquoise"
                    maxLength={100}
                    disabled={isSavingName}
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={isSavingName}
                    className="p-1.5 text-turquoise hover:bg-turquoise/10 rounded-lg transition-colors shrink-0"
                  >
                    {isSavingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => setIsEditingName(false)}
                    disabled={isSavingName}
                    className="p-1.5 text-white/40 hover:text-white/60 hover:bg-white/10 rounded-lg transition-colors shrink-0 disabled:opacity-30"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-2">
                  <p className="font-semibold text-white truncate">
                    {subscriber?.name || 'Usuário'}
                  </p>
                  <button
                    onClick={() => {
                      setEditName(subscriber?.name || '');
                      setIsEditingName(true);
                    }}
                    className="p-1 text-white/40 hover:text-turquoise hover:bg-turquoise/10 rounded-lg transition-colors shrink-0"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <p className="text-sm text-white/50 truncate mb-2">
                {user?.email}
              </p>
              <button
                onClick={() => navigate('/connect')}
                className="text-sm text-turquoise hover:text-turquoise/80 transition-colors"
              >
                {subscriber?.letzplayUsername
                  ? `@${subscriber.letzplayUsername}`
                  : 'Importar perfil da plataforma de torneios'}
              </button>
            </div>
          </div>

          {/* CPF + birth date (needed for paid rankings via Asaas) */}
          <div className="mt-4 pt-4 border-t border-white/5 space-y-3">
            <InlineProfileField
              label="CPF"
              value={subscriber?.cpf ? maskCpfDisplay(subscriber.cpf) : null}
              placeholder="000.000.000-00"
              inputMode="numeric"
              formatInput={maskCpfDisplay}
              onSave={async (raw) => {
                const response = await api.auth.updateProfile({ cpf: raw.replace(/\D/g, '') });
                if (response.data?.subscriber) setSubscriber(response.data.subscriber);
                return response.success ? null : response.error?.message || 'CPF inválido';
              }}
            />
            <InlineProfileField
              label="Nascimento"
              value={subscriber?.birthDate ? formatDateBR(subscriber.birthDate) : null}
              type="date"
              onSave={async (value) => {
                const response = await api.auth.updateProfile({ birthDate: value });
                if (response.data?.subscriber) setSubscriber(response.data.subscriber);
                return response.success ? null : response.error?.message || 'Data inválida';
              }}
            />
            <p className="text-[11px] text-white/30 leading-snug">
              Usados apenas para gerar cobranças PIX ao entrar em rankings pagos.
            </p>
          </div>
        </Card>

        {/* Payments (Asaas connection for ranking admins) */}
        <AsaasCard />

        {/* App */}
        <Card delay={0.25}>
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-turquoise" />
            App
          </h3>

          {isInstalled ? (
            <p className="text-sm text-white/60 mb-4 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-success-green shrink-0" />
              Você está usando o BT Vision como app
            </p>
          ) : canInstall ? (
            <div className="mb-4">
              <p className="text-sm text-white/60 mb-3">
                Instale o BT Vision na sua tela inicial para acesso rápido.
              </p>
              <Button
                onClick={isIOS ? undefined : install}
                variant="secondary"
                fullWidth
              >
                <Download className="w-4 h-4" />
                <span>{isIOS ? 'Ver instruções de instalação' : 'Instalar app'}</span>
              </Button>
            </div>
          ) : (
            <p className="text-sm text-white/60 mb-4">
              Acesse pelo Safari (iOS) ou Chrome (iOS/Android) para instalar o app.
            </p>
          )}

          <Button
            onClick={() => navigate('/reset')}
            variant="ghost"
            fullWidth
            className="text-warning-orange hover:bg-warning-orange/10"
          >
            <Trash2 className="w-4 h-4" />
            <span>Resetar app</span>
          </Button>
          <p className="text-xs text-white/30 mt-2 text-center">
            Limpa cache, dados salvos e reconecta do zero
          </p>
        </Card>

        {/* Notifications (per device — a subscription belongs to this browser) */}
        <NotificationsCard />

        {/* Sign Out */}
        <Card delay={0.35}>
          <Button
            onClick={handleSignOut}
            variant="ghost"
            fullWidth
            className="text-coral hover:bg-coral/10"
          >
            <LogOut className="w-5 h-5" />
            <span>Sair da conta</span>
          </Button>
        </Card>
      </div>

    </AppShell>
  );
}

// ============================================================================
// Profile helpers (CPF + birth date for paid rankings)
// ============================================================================

function maskCpfDisplay(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

function formatDateBR(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  return d && m && y ? `${d}/${m}/${y}` : isoDate;
}

/** Inline label/value row with the same edit-save-cancel trio as the name field. */
function InlineProfileField({
  label,
  value,
  placeholder,
  type = 'text',
  inputMode,
  formatInput,
  onSave,
}: {
  label: string;
  value: string | null;
  placeholder?: string;
  type?: 'text' | 'date';
  inputMode?: 'numeric';
  formatInput?: (v: string) => string;
  onSave: (value: string) => Promise<string | null>;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!editValue.trim()) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const err = await onSave(editValue.trim());
      if (err) {
        setError(err);
      } else {
        setIsEditing(false);
      }
    } catch {
      setError('Erro ao salvar');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-white/60 shrink-0">{label}</span>
        {isEditing ? (
          <div className="flex items-center gap-1.5">
            <input
              type={type}
              inputMode={inputMode}
              value={editValue}
              placeholder={placeholder}
              autoFocus
              max={type === 'date' ? new Date().toISOString().slice(0, 10) : undefined}
              onChange={(e) =>
                setEditValue(formatInput ? formatInput(e.target.value) : e.target.value)
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') setIsEditing(false);
              }}
              className="bg-white/10 text-white rounded-lg px-2 py-1 text-sm w-40 min-w-0 min-h-[28px] appearance-none outline-none focus:ring-1 focus:ring-turquoise [color-scheme:dark] [&::-webkit-date-and-time-value]:text-left"
              disabled={isSaving}
            />
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="p-1.5 text-turquoise hover:bg-turquoise/10 rounded-lg transition-colors shrink-0"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setIsEditing(false)}
              disabled={isSaving}
              className="p-1.5 text-white/40 hover:text-white/60 hover:bg-white/10 rounded-lg transition-colors shrink-0 disabled:opacity-30"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`text-sm truncate ${value ? 'text-white' : 'text-white/30'}`}>
              {value || 'Não informado'}
            </span>
            <button
              onClick={() => {
                setEditValue('');
                setError(null);
                setIsEditing(true);
              }}
              className="p-1 text-white/40 hover:text-turquoise hover:bg-turquoise/10 rounded-lg transition-colors shrink-0"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      {error && <p className="text-coral text-xs mt-1 text-right">{error}</p>}
    </div>
  );
}

// ============================================================================
// Push notifications card (per device)
// ============================================================================

function NotificationsCard() {
  const { isSupported, needsInstall, permission, isSubscribed, isWorking, subscribe, unsubscribe } =
    usePushNotifications();
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  // Nothing to offer: the browser cannot do push, the server has it off, or we
  // are on the legacy origin — where a subscription would die at domain sunset
  // and the MigrationBanner owns the message anyway.
  if (!isSupported && !needsInstall) return null;

  const handleSubscribe = async () => {
    setMessage(null);
    const ok = await subscribe();
    if (!ok) {
      setMessage({
        kind: 'error',
        text:
          Notification.permission === 'denied'
            ? 'Permissão negada pelo navegador.'
            : 'Não foi possível ativar as notificações.',
      });
    }
  };

  const handleUnsubscribe = async () => {
    setMessage(null);
    const ok = await unsubscribe();
    if (!ok) setMessage({ kind: 'error', text: 'Não foi possível desativar.' });
  };

  const handleTest = async () => {
    setIsTesting(true);
    setMessage(null);
    try {
      const res = await api.vision.push.sendTest();
      trackEvent('btv_push_test_sent');
      if (res.success && (res.data?.sent ?? 0) > 0) {
        setMessage({ kind: 'ok', text: 'Enviamos uma notificação de teste.' });
      } else {
        setMessage({ kind: 'error', text: 'Nenhum aparelho recebeu o teste.' });
      }
    } catch {
      setMessage({ kind: 'error', text: 'Erro ao enviar o teste.' });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Card delay={0.3}>
      <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
        <Bell className="w-5 h-5 text-turquoise" />
        Notificações
      </h3>

      {needsInstall ? (
        <>
          {/* iOS grants the Push API to home-screen apps only. */}
          <p className="text-sm text-white/60 mb-3">
            Instale o app na tela inicial para receber notificações no iPhone/iPad.
          </p>
          <Button onClick={() => setShowIOSGuide(true)} variant="secondary" fullWidth>
            <Download className="w-4 h-4" />
            <span>Ver instruções de instalação</span>
          </Button>
        </>
      ) : permission === 'denied' ? (
        // The browser will not prompt again once blocked — only its settings can undo this.
        <p className="text-sm text-white/60">
          Notificações bloqueadas. Para ativar, permita notificações do BT Vision nos
          ajustes do navegador ou do celular.
        </p>
      ) : isSubscribed ? (
        <>
          <p className="text-sm text-success-green mb-4 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 shrink-0" />
            Notificações ativadas neste aparelho
          </p>
          <div className="flex gap-2">
            <Button onClick={handleTest} variant="secondary" size="sm" fullWidth isLoading={isTesting}>
              Enviar teste
            </Button>
            <Button
              onClick={handleUnsubscribe}
              variant="ghost"
              size="sm"
              fullWidth
              isLoading={isWorking}
              className="text-coral hover:bg-coral/10"
            >
              Desativar
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-white/60 mb-4">
            Receba avisos quando uma rodada for liberada ou as inscrições abrirem.
          </p>
          <Button onClick={handleSubscribe} variant="secondary" fullWidth isLoading={isWorking}>
            <Bell className="w-4 h-4" />
            <span>Ativar notificações</span>
          </Button>
        </>
      )}

      <p className="text-[11px] text-white/30 leading-snug mt-3">
        Valem só para este aparelho — ative de novo em cada celular ou computador.
      </p>

      {message && (
        <p className={`text-xs mt-3 ${message.kind === 'ok' ? 'text-success-green' : 'text-coral'}`}>
          {message.text}
        </p>
      )}

      <AnimatePresence>
        {showIOSGuide && <IOSInstallGuide onClose={() => setShowIOSGuide(false)} />}
      </AnimatePresence>
    </Card>
  );
}

// ============================================================================
// Asaas connection card (receive ranking entry fees)
// ============================================================================

function AsaasCard() {
  const [status, setStatus] = useState<AsaasStatus | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [showConnect, setShowConnect] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.asaas
      .status()
      .then((res) => {
        if (!cancelled && res.success && res.data) setStatus(res.data.status);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const handleConnect = async () => {
    if (!apiKey.trim()) return;
    setIsWorking(true);
    setMessage(null);
    try {
      const res = await api.asaas.connect(apiKey.trim());
      if (res.success && res.data) {
        setStatus(res.data.status);
        setApiKey('');
        setShowConnect(false);
        setMessage({ kind: 'ok', text: 'Conta Asaas conectada!' });
      } else {
        setMessage({ kind: 'error', text: res.error?.message || 'Chave de API inválida' });
      }
    } catch {
      setMessage({ kind: 'error', text: 'Erro ao conectar com o Asaas' });
    } finally {
      setIsWorking(false);
    }
  };

  const handleDisconnect = async () => {
    setIsWorking(true);
    setMessage(null);
    try {
      const res = await api.asaas.disconnect();
      if (res.success) {
        setStatus({ connected: false });
        setMessage({ kind: 'ok', text: 'Conta Asaas desconectada' });
      } else {
        setMessage({ kind: 'error', text: res.error?.message || 'Não foi possível desconectar' });
      }
    } catch {
      setMessage({ kind: 'error', text: 'Erro ao desconectar' });
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Card delay={0.18}>
      <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
        <QrCode className="w-5 h-5 text-turquoise" />
        Pagamentos
      </h3>

      <p className="text-sm text-white/60 mb-4">
        Conecte sua conta Asaas para cobrar inscrição via PIX nos rankings que você criar.
        {status?.platformFeePercent
          ? ` O BT Vision retém ${status.platformFeePercent}% de taxa de serviço sobre cada inscrição paga.`
          : ''}
      </p>

      {status?.connected ? (
        <>
          <p className="text-sm text-success-green mb-1 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 shrink-0" />
            Conta Asaas conectada
            {status.environment === 'sandbox' && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-sunny/10 text-sunny">
                Sandbox
              </span>
            )}
          </p>
          <p className="text-xs text-white/40 mb-4">
            Dica: cadastre uma chave PIX no Asaas para QR Codes de longa duração.
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowConnect(!showConnect)}
              variant="secondary"
              size="sm"
              fullWidth
            >
              Trocar chave
            </Button>
            <Button
              onClick={handleDisconnect}
              variant="ghost"
              size="sm"
              fullWidth
              isLoading={isWorking && !showConnect}
              className="text-coral hover:bg-coral/10"
            >
              Desconectar
            </Button>
          </div>
        </>
      ) : (
        !showConnect && (
          <Button onClick={() => setShowConnect(true)} variant="secondary" fullWidth>
            Conectar conta Asaas
          </Button>
        )
      )}

      {showConnect && (
        <div className="mt-3 space-y-2">
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Chave de API do Asaas"
            className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white text-sm placeholder-white/25 focus:outline-none focus:border-turquoise/50"
          />
          <Button
            onClick={handleConnect}
            size="sm"
            fullWidth
            isLoading={isWorking}
            disabled={!apiKey.trim()}
          >
            Conectar
          </Button>
          <p className="text-[11px] text-white/40 leading-snug">
            Asaas → Integrações → Chave de API. A chave fica criptografada e é usada
            apenas para gerar as cobranças dos seus rankings.
          </p>
        </div>
      )}

      {message && (
        <p className={`text-xs mt-3 ${message.kind === 'ok' ? 'text-success-green' : 'text-coral'}`}>
          {message.text}
        </p>
      )}
    </Card>
  );
}
